const GA_MEASUREMENT_ID = "G-CZ9PCR2P7M";
const PRODUCTION_HOSTNAME = "friel.frievegames.com";
const DEBUG_QUERY_PARAM = "ga_debug";
const GA_SCRIPT_URL = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
const TRACKED_CUSTOM_EVENTS = new Set(["game_start", "game_over", "share", "high_score_prompt", "high_score_submit"]);

type AnalyticsValue = string | number | boolean | null | undefined;
type AnalyticsParams = Record<string, AnalyticsValue>;
type Gtag = (...args: unknown[]) => void;
type AnalyticsStatus = {
  enabled: boolean;
  debugMode: boolean;
  hostname: string;
  protocol: string;
  scriptAppended: boolean;
  scriptLoaded: boolean;
  scriptError: boolean;
  commandCount: number;
  lastCommand: string | null;
  lastEvent: string | null;
  usesOfficialArgumentsWrapper: boolean;
};

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
    __FRIEL_ANALYTICS_ENABLED__?: boolean;
    __FRIEL_ANALYTICS_STATUS__?: AnalyticsStatus;
    __frielGoogleTagConfigured?: boolean;
  }
}

export function shouldEnableAnalytics(location: Pick<Location, "hostname" | "protocol">): boolean {
  return location.protocol === "https:" && location.hostname === PRODUCTION_HOSTNAME;
}

export function shouldEnableAnalyticsDebug(location: Pick<Location, "search">): boolean {
  return new URLSearchParams(location.search).get(DEBUG_QUERY_PARAM) === "1";
}

export function shouldTrackCustomEvent(name: string): boolean {
  return TRACKED_CUSTOM_EVENTS.has(name);
}

export function installAnalytics(): void {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return;
  }

  const debugMode = shouldEnableAnalyticsDebug(window.location);
  setAnalyticsStatus({
    enabled: false,
    debugMode,
    hostname: window.location.hostname,
    protocol: window.location.protocol,
    scriptAppended: false,
    scriptLoaded: false,
    scriptError: false,
    commandCount: 0,
    lastCommand: null,
    lastEvent: null,
    usesOfficialArgumentsWrapper: true
  });

  if (!shouldEnableAnalytics(window.location)) {
    window.__FRIEL_ANALYTICS_ENABLED__ = false;
    window.gtag ??= () => undefined;
    return;
  }

  if (window.__FRIEL_ANALYTICS_ENABLED__) {
    return;
  }

  window.__FRIEL_ANALYTICS_ENABLED__ = true;
  setAnalyticsStatus({ enabled: true });
  window.dataLayer = window.dataLayer ?? [];
  window.gtag = window.gtag ?? function gtag(): void {
    window.dataLayer?.push(arguments);
  };

  ensureGoogleTagScript();

  if (!window.__frielGoogleTagConfigured) {
    sendGtagCommand("js", new Date());
    sendGtagCommand("config", GA_MEASUREMENT_ID, scrubParams({ ...pageParams(), debug_mode: debugMode ? true : undefined }));
    window.__frielGoogleTagConfigured = true;
  } else {
    setAnalyticsStatus({
      commandCount: countGtagCommands(),
      lastCommand: "config"
    });
  }
}

export function trackEvent(name: string, params: AnalyticsParams = {}): void {
  if (typeof window === "undefined" || !shouldTrackCustomEvent(name) || !window.__FRIEL_ANALYTICS_ENABLED__ || !window.gtag) {
    return;
  }
  sendGtagCommand("event", name, scrubParams({ ...params, debug_mode: shouldEnableAnalyticsDebug(window.location) ? true : undefined }));
}

function pageParams(): AnalyticsParams {
  return {
    page_title: document.title,
    page_location: window.location.href,
    page_path: `${window.location.pathname}${window.location.search}`
  };
}

function scrubParams(params: AnalyticsParams): Record<string, string | number | boolean | null> {
  const scrubbed: Record<string, string | number | boolean | null> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) {
      scrubbed[key] = value;
    }
  }
  return scrubbed;
}

function sendGtagCommand(...args: unknown[]): void {
  window.gtag?.(...args);
  const [command, eventName] = args;
  setAnalyticsStatus({
    commandCount: (window.__FRIEL_ANALYTICS_STATUS__?.commandCount ?? 0) + 1,
    lastCommand: typeof command === "string" ? command : null,
    lastEvent: command === "event" && typeof eventName === "string" ? eventName : (window.__FRIEL_ANALYTICS_STATUS__?.lastEvent ?? null)
  });
}

function ensureGoogleTagScript(): void {
  const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${GA_SCRIPT_URL}"]`);
  if (existingScript) {
    existingScript.addEventListener("load", () => {
      setAnalyticsStatus({ scriptLoaded: true });
    });
    existingScript.addEventListener("error", () => {
      setAnalyticsStatus({ scriptError: true });
    });
    setAnalyticsStatus({ scriptAppended: true, scriptLoaded: hasGtmLoadEvent() });
    return;
  }

  const script = document.createElement("script");
  script.async = true;
  script.src = GA_SCRIPT_URL;
  script.addEventListener("load", () => {
    setAnalyticsStatus({ scriptLoaded: true });
  });
  script.addEventListener("error", () => {
    setAnalyticsStatus({ scriptError: true });
  });
  document.head.appendChild(script);
  setAnalyticsStatus({ scriptAppended: true });
}

function countGtagCommands(): number {
  return (window.dataLayer ?? []).filter((entry) => {
    if (Array.isArray(entry)) {
      return true;
    }
    return Object.prototype.toString.call(entry) === "[object Arguments]";
  }).length;
}

function hasGtmLoadEvent(): boolean {
  return (window.dataLayer ?? []).some((entry) => {
    if (!entry || typeof entry !== "object") {
      return false;
    }
    return (entry as { event?: unknown }).event === "gtm.load";
  });
}

function setAnalyticsStatus(nextStatus: Partial<AnalyticsStatus>): void {
  if (typeof window === "undefined") {
    return;
  }
  window.__FRIEL_ANALYTICS_STATUS__ = {
    enabled: false,
    debugMode: false,
    hostname: "",
    protocol: "",
    scriptAppended: false,
    scriptLoaded: false,
    scriptError: false,
    commandCount: 0,
    lastCommand: null,
    lastEvent: null,
    usesOfficialArgumentsWrapper: true,
    ...window.__FRIEL_ANALYTICS_STATUS__,
    ...nextStatus
  };
}
